import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';

/**
 * The wall that goes up when a workspace stops accepting entries.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A DIALOG AND NOT A BANNER
 *
 * A banner is the right shape for "this needs attention soon". It is the wrong
 * shape for "nothing you type will save". Until now the only sign was a small
 * red chip in the top bar, so the way a customer found out was: fill in an
 * invoice, press Save, get a 402 from the server and lose the form. Being told
 * before the work is thrown away is the entire point.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS NOT DISMISSIBLE, AND IT IS NOT A TRAP
 *
 * No close button, because dismissing it would restore a screen that cannot
 * save anything: the illusion is what does the damage. But it does not cover
 * the app either. Reading, exporting and downloading every document all still
 * work, and the dialog says so and offers the way out. That is the promise on
 * the pricing page, and a lockout screen that hid the customer's own books
 * while asking them for money would break it at exactly the moment it matters.
 *
 * TWO THINGS IT MUST NEVER DO, both found by walking through it:
 *
 *   It must not cover /billing. Rendered globally and undismissible, the wall
 *   would sit on top of the one page that takes the payment: the customer is
 *   told to fix it and then prevented from fixing it.
 *
 *   It must not be permanent. "Keep reading" stands it down for the session, so
 *   the promise that a lapsed workspace stays readable and exportable is a
 *   promise the interface actually keeps. It returns on the next load, because
 *   the situation has not changed and a customer who forgets is a customer who
 *   loses work at the next Save.
 */
const DISMISS_KEY = 'railit_write_block_seen';
export function WriteBlockedDialog() {
  const { entitlements } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (!entitlements || entitlements.can_write) return null;
  /* Never over the page that fixes it. */
  if (pathname.startsWith('/billing')) return null;

  /*
    A LAPSED TRIAL CAN BE SET ASIDE. AN UNPAID BILL CANNOT.

    Twelve days after a failed payment the workspace is not merely read-only,
    it is in arrears, and the product says so by refusing to get out of the way:
    every user of that company sees this screen and the billing page, and
    nothing else. Someone whose trial simply ended is not in debt to anybody,
    so they may push the dialog aside and go on reading their own books.
  */
  const hard = entitlements.locked || entitlements.on_hold;
  if (!hard && dismissed) return null;

  const reason = entitlements.reason ?? 'subscription_inactive';
  const trialOver = reason === 'trial_expired';
  const onHold = entitlements.on_hold;

  /*
    The heading names the cause. "Your workspace is read-only" is true for all
    of them and useless for each: the person whose trial ran out has a different
    next action from the person whose card was declined.
  */
  const title = onHold
    ? t('hold_title')
    : trialOver
      ? t('blocked_trial_title')
      : t('blocked_payment_title');
  const body = onHold
    ? t('hold_body').replace('{fee}', `USD ${entitlements.restore_fee_usd}`)
    : trialOver
      ? t('blocked_trial_body')
      : t('blocked_payment_body');
  const cta = onHold ? t('hold_cta') : trialOver ? t('blocked_trial_cta') : t('blocked_payment_cta');

  /* Locked, not yet held: say when the workspace goes dormant. A date is what
     makes "sort this out" a decision rather than a worry. */
  const holdWarning =
    entitlements.locked && !onHold && entitlements.hold_at
      ? t('hold_warning').replace(
          '{date}',
          new Date(entitlements.hold_at).toLocaleDateString(lang === 'ar' ? 'ar-BH' : 'en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }),
        )
      : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="write-blocked-title"
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
      /* Above the shell, the agent rail and any sheet. `backdrop-blur` rather
         than an opaque cover: the workspace stays visible behind it, which is
         the difference between "locked" and "gone". */
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-7 shadow-[var(--elev-3)]">
        <div className="mb-5 flex items-center gap-3">
          <Logo size={26} markOnly />
          <span className="ms-auto inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11.5px] text-muted-foreground">
            <Lock className="size-3" aria-hidden />
            {t('blocked_badge')}
          </span>
        </div>

        <h2 id="write-blocked-title" className="text-[19px] font-semibold leading-snug text-foreground">
          {title}
        </h2>
        <p className="mt-2.5 text-[14px] leading-relaxed text-muted-foreground">{body}</p>

        {holdWarning && (
          <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3.5 text-[13px] leading-relaxed text-amber-800 dark:text-amber-500">
            {holdWarning}
          </div>
        )}

        <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
          {onHold ? t('hold_note') : t('blocked_readonly_note')}
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={() => navigate("/billing")} className="w-full justify-center">
            <CreditCard className="size-4" aria-hidden />
            {cta}
          </Button>
          {/* Not a dismissal. It moves them to the one page that can fix it,
              and the dialog reappears everywhere else until it is fixed. */}
          {!hard && (
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.setItem(DISMISS_KEY, '1');
              } catch {
                /* private browsing; it simply reappears on the next route */
              }
              setDismissed(true);
            }}
            className="w-full rounded-md py-2 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('blocked_keep_reading')}
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
